import { ImageScaleView, View, ViewOpts } from '@play-co/timestep-core/ui';
import LangBitmapFontTextView from 'src/lib/ui/components/LangBitmapFontTextView';
import i18n from 'src/lib/i18n/i18n';
import { now } from 'src/StateObserver';
import { createWaiter } from 'src/lib/utils';
import { duration } from 'src/replicant/utils/duration';
import { formatDuration } from 'src/lib/formatDuration';

type TimerOpts = {
  bg?: boolean;
  textColor?: string;
  textSize?: number;
  noIcon?: boolean;
  roundUp?: boolean;
  customIcon?: string;
  endedText?: string;
};

export class Match2Timer extends View {
  private readonly bg: ImageScaleView;
  private readonly icon?: ImageScaleView;
  public readonly text: LangBitmapFontTextView;
  private getRemainingTime: (now: number) => number = () => 0;
  private waiter?: ReturnType<typeof createWaiter>;
  private roundUp: boolean;
  private onUpdate?: (remainingTime: number) => void;
  private onTimerEnd?: () => void; // Callback for when the timer ends
  private isTimerEnded = false; // Flag to track if the timer has ended
  public endedText: string;

  constructor(opts: ViewOpts & TimerOpts) {
    super({ width: 202, height: 52, ...opts });

    this.roundUp = opts.roundUp ?? false;
    this.endedText = opts.endedText ?? i18n('match2.timer.ended');

    this.bg = new ImageScaleView({
      superview: this,
      width: this.style.width,
      height: this.style.height,
      image: 'assets/ui/match2/common/timer_base.png',
      scaleMethod: '3slice',
      sourceSlices: {
        horizontal: { left: 30, right: 30 },
      },
    });

    if (!opts.noIcon) {
      this.icon = new ImageScaleView({
        superview: this,
        width: 36,
        height: 36,
        x: 6,
        y: 5,
        image: opts.customIcon ?? 'assets/ui/match2/common/timer_icon.png',
        scaleMethod: 'contain',
      });
    }

    if (opts.bg !== undefined) {
      this.bg.updateOpts({ visible: opts.bg });
    }

    this.text = new LangBitmapFontTextView({
      superview: this,
      x: 110,
      y: 23,
      centerOnOrigin: true,
      align: 'center',
      verticalAlign: 'center',
      size: 24,
      font: 'Body',
      width: 110,
      height: 30,
      color: '#BF8147',
    });

    if (opts.textColor !== undefined) {
      this.text.updateOpts({ color: opts.textColor });
    }

    if (opts.textSize !== undefined) {
      this.text.updateOpts({ size: opts.textSize });
    }
  }

  public autoUpdate() {
    this.waiter?.clear();
    this.waiter = createWaiter();
    this.waiter.wait(this.autoUpdate.bind(this), duration({ seconds: 1 }));
    this.update();
  }

  public stopAutoUpdate() {
    this.waiter?.clear();
    this.waiter = undefined;
  }

  public update() {
    this.icon?.updateOpts({ visible: true });
    this.text.updateOpts({ x: 110, width: 110 });
    const remainingTime = this.getRemainingTime(now());
    if (remainingTime > 0) {
      this.text.localeText = () => formatDuration(remainingTime, this.roundUp);
      this.isTimerEnded = false;
    } else {
      this.text.localeText = () => this.endedText;
      //transition to lost game popup
      if (!this.isTimerEnded) {
        this.isTimerEnded = true;
        this.onTimerEnd?.();
      }
    }
    this.onUpdate?.(remainingTime);
  }

  public setMessage(message: string, opts?: ViewOpts) {
    this.text.localeText = () => message;
    this.text.updateOpts({ x: 74, width: 160, ...opts });
    this.icon?.updateOpts({ visible: false });
  }

  public setRemainingTimeGetter(getRemainingTime: (now: number) => number) {
    this.getRemainingTime = getRemainingTime;
  }

  public setOnUpdate(onUpdate?: (remainingTime: number) => void) {
    this.onUpdate = onUpdate;
  }

  public setOnTimerEnd(onTimerEnd: () => void) {
    this.onTimerEnd = onTimerEnd;
  }
}
